import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { useAuth } from '../firebase/FirebaseContext';
import { useAuthSnapshot } from './useAuthSnapshot';
import { useFriendsList } from '../../components/landing/dashboard/useFriendsList';
import { readHasBoardsHint, writeHasBoardsHint } from '../lib/hasBoardsHint';
import { isV4Preview } from '../lib/v4Preview';
import { GUIDES_INDEX_PATH, useGuidesIndexHref } from '../hub/giftGuideHubFlag';

/**
 * Right-grouped desktop nav items for SiteHeader.
 *
 * The avatar (HeaderAccountMenu) is NOT a nav item — it's a separate
 * component rendered to the right of these items. So this hook only
 * surfaces text-link items.
 *
 * Gating:
 *  - People link always visible (its target /#your-people exists for
 *    every visitor; first-time visitors see it scroll to the marketing
 *    section).
 *  - Calendar + Gift Guides links visible only when the user has ≥1
 *    recipient. Same condition as MobileTabBarMount + the existing
 *    BrowseMyFriendsSection — the whole nav shell appears together once
 *    the user is "in the product."
 *
 * The active-state flag is derived from useLocation so the underline
 * follows the current route without consumers having to wire it.
 *
 * Pop-in guard (header/IA spec v5 PR 2): the friends list re-subscribes on
 * every mount, so gating strictly on `hydrated` made the links appear a
 * beat AFTER the header on every load — a visible reflow, and it broke the
 * "header identical pre/post chunk-load" rule for the route-shell fallback.
 * Until hydration lands we defer to the persisted has-boards hint
 * (hasBoardsHint.ts — same mechanism as LandingPage's layout gate), so
 * returning board-users get their links on the first frame and first-time
 * visitors still see none. The live count corrects the hint once hydrated.
 */

export interface DesktopNavItem {
  label: string;
  to: string;
  isActive: boolean;
}

export function useDesktopNav(): DesktopNavItem[] {
  const auth = useAuth();
  const location = useLocation();
  // v4 (journey 6): a signed-in permanent account unlocks the nav even with no
  // boards yet (navUnlocked). The anon→permanent upgrade happens IN PLACE
  // (linkWithPopup / linkWithCredential — same uid, isAnonymous flips) and is
  // delivered ONLY via onIdTokenChanged; onAuthStateChanged is silent, so a
  // single-listener read left the nav hidden until a hard refresh. useAuthSnapshot
  // subscribes to BOTH observers — see useAuthSnapshot.ts.
  const { uid, isPermanent: isSignedIn } = useAuthSnapshot(auth);

  const { friends, hydrated } = useFriendsList(uid);

  // Read once per mount; keep it fresh for the next mount.
  // H7 (THE-581): the desktop twin of the bottom tab bar's Gift Guides link.
  // Carries `?giftGuideHub=1` for a preview tester and resolves to the bare
  // path for everyone else, so the rendered nav is unchanged without the param.
  const guidesHref = useGuidesIndexHref();

  const [hadBoardsHint] = useState(readHasBoardsHint);
  useEffect(() => {
    if (!hydrated) return;
    writeHasBoardsHint(friends.length > 0);
  }, [hydrated, friends.length]);

  return useMemo(() => {
    // Both items are gated on the same condition: user has at least one
    // recipient — live count once hydrated, persisted hint while the
    // listener is still catching up (see the pop-in guard note above).
    // A brand-new user with nothing in their account shouldn't see nav
    // links suggesting destinations they haven't earned yet.
    const hasBoards = hydrated ? friends.length > 0 : hadBoardsHint;
    // v4 journey 6: signed in unlocks the nav even with nothing saved, so a
    // just-signed-in user isn't stranded on a header with no links. Gated
    // behind the flag → flag-off byte-identical to production.
    const showNav = hasBoards || (isV4Preview() && isSignedIn);
    if (!showNav) {
      return [];
    }

    return [
      {
        label: 'People',
        to: '/#your-people',
        // Active on homepage AND on a board itself: a board is one specific
        // person's page, so "People" is the nav section it belongs to
        // (Kate's mock, THE-1185 QA round 4, #889). Boards render at
        // /quiz/results/:recipientId/:recommendationId — /board/:recipientId
        // is only BoardRoute's redirect shim to that URL, so both prefixes
        // are matched (shepherd QA round 5, #889: /board/ alone never
        // matches a live board). Explicitly NOT active on /calendar so a
        // single underline shows at any time.
        isActive:
          location.pathname === '/' ||
          location.pathname.startsWith('/board/') ||
          location.pathname.startsWith('/quiz/results/'),
      },
      {
        label: 'Calendar',
        to: '/calendar',
        isActive: location.pathname === '/calendar',
      },
      {
        label: 'Gift Guides',
        to: guidesHref,
        // Compare the PATHNAME, not the href: the cohort param lives in the
        // query string, so an `=== guidesHref` test here would break the
        // active underline for exactly the testers carrying it.
        isActive: location.pathname === GUIDES_INDEX_PATH,
      },
    ];
  }, [friends.length, hydrated, hadBoardsHint, isSignedIn, location.pathname, guidesHref]);
}
